/**
 * 全局国家/地区树（treeList）唯一缓存入口。
 *
 * - 全站仅通过本模块触发 GET /biz/overseas/common/globalcountry/treeList
 * - 业务组件只读 globalCountryTreeData / globalCountryCascaderOptions，或调用下方工具方法
 */
import { secureLocalStorage } from '@/utils/secureLocalStorage'
import { ref } from 'vue'
import {
  fetchGlobalCountryTreeList,
  findGeonameidPathInCascaderTree,
  formatGeonameidAsAreaPath,
  GLOBAL_COUNTRY_TREE_CACHE_TTL_MS,
  lastGlobalCountryTreeServedAt,
  mapGlobalAreaTreeToCascaderOptions,
  readPersistedGlobalCountryTreeList,
  type GlobalAreaTreeNode,
  type OverseasGeoCascaderOption,
} from './globalCountryTree'

/** 全局国家树是否已就绪（含持久化回填） */
export const globalCountryTreeLoaded = ref(false)
export const globalCountryTreeLoading = ref(false)
/** 原始树（treeList） */
export const globalCountryTreeData = ref<GlobalAreaTreeNode[]>([])
/** 级联 options（由树映射，与人员/企业表单共用） */
export const globalCountryCascaderOptions = ref<OverseasGeoCascaderOption[]>([])

let lastRefreshTime = 0
let lastFetchFailedAt = 0
const CACHE_TTL_MS = GLOBAL_COUNTRY_TREE_CACHE_TTL_MS
/** 请求失败后在此时间内不再重试，避免接口故障时反复打 treeList */
const FETCH_RETRY_MS = 5 * 60 * 1000
let loadInflight: Promise<void> | null = null
let backgroundRefresh: Promise<void> | null = null

function applyTreeToRefs(data: GlobalAreaTreeNode[], refreshedAt = Date.now()): void {
  globalCountryTreeData.value = data
  globalCountryCascaderOptions.value = mapGlobalAreaTreeToCascaderOptions(data)
  globalCountryTreeLoaded.value = data.length > 0
  if (data.length > 0) {
    lastRefreshTime = refreshedAt
  }
}

/** 模块加载时：从 localStorage 回填，避免各页面重复打 treeList */
function hydrateFromPersistedStorage(): void {
  if (globalCountryTreeLoaded.value && globalCountryTreeData.value.length) return
  const persisted = readPersistedGlobalCountryTreeList()
  if (persisted?.data?.length) {
    applyTreeToRefs(persisted.data, persisted.at)
  }
}

hydrateFromPersistedStorage()

export function needRefreshCache(): boolean {
  if (!globalCountryTreeLoaded.value) return true
  return Date.now() - lastRefreshTime > CACHE_TTL_MS
}

/** 确保 treeList 已加载（命中缓存则不发请求；过期缓存先展示，后台再刷新） */
export const ensureGlobalCountryTree = loadGlobalCountryTree

function refreshTreeInBackground(): void {
  if (backgroundRefresh) return
  backgroundRefresh = (async () => {
    try {
      const data = await fetchGlobalCountryTreeList(undefined, true)
      if (data.length) applyTreeToRefs(data, Date.now())
    } catch (error) {
      console.error('Failed to refresh global country tree:', error)
    } finally {
      backgroundRefresh = null
    }
  })()
}

/** 加载全局国家/地区树（命中缓存则不发请求；并发共用同一 Promise） */
export async function loadGlobalCountryTree(forceRefresh = false): Promise<void> {
  if (!globalCountryTreeLoaded.value) {
    hydrateFromPersistedStorage()
  }
  if (!forceRefresh && globalCountryTreeLoaded.value && !needRefreshCache()) {
    return
  }
  if (!forceRefresh && globalCountryTreeLoaded.value) {
    refreshTreeInBackground()
    return
  }
  if (
    !forceRefresh &&
    !globalCountryTreeLoaded.value &&
    lastFetchFailedAt > 0 &&
    Date.now() - lastFetchFailedAt < FETCH_RETRY_MS
  ) {
    return
  }
  if (loadInflight) {
    return loadInflight
  }

  loadInflight = (async () => {
    const showSpinner = globalCountryTreeData.value.length === 0
    if (showSpinner) globalCountryTreeLoading.value = true
    try {
      const data = await fetchGlobalCountryTreeList(undefined, forceRefresh)
      if (data.length) applyTreeToRefs(data, lastGlobalCountryTreeServedAt() || Date.now())
      lastFetchFailedAt = data.length ? 0 : Date.now()
      if (globalCountryTreeLoaded.value && needRefreshCache()) {
        refreshTreeInBackground()
      }
    } catch (error) {
      console.error('Failed to load global country tree:', error)
      lastFetchFailedAt = Date.now()
      if (!globalCountryTreeData.value.length) {
        hydrateFromPersistedStorage()
      }
    } finally {
      globalCountryTreeLoading.value = false
      loadInflight = null
    }
  })()

  return loadInflight
}

export function clearGlobalCountryTreeCache(): void {
  globalCountryTreeData.value = []
  globalCountryCascaderOptions.value = []
  globalCountryTreeLoaded.value = false
  lastRefreshTime = 0
  lastFetchFailedAt = 0
  loadInflight = null
  backgroundRefresh = null
  try {
    secureLocalStorage.keys().forEach((key) => {
      if (key.startsWith('yj:globalCountryTree:')) {
        secureLocalStorage.removeItem(key)
      }
    })
  } catch {
    // ignore
  }
}

/** 按 geonameid 从缓存树解析「国家 / 省 / …」展示文案 */
export function formatGeonameidAreaPathFromCache(geonameid: number | null | undefined): string {
  if (!globalCountryTreeData.value.length) return ''
  return formatGeonameidAsAreaPath(globalCountryTreeData.value, geonameid)
}

/** 按末级 geonameid 从缓存级联 options 反查 value 路径 */
export function findGeonameidCascaderPathFromCache(
  geonameid: number | string | null | undefined,
): string[] | null {
  if (geonameid == null || !globalCountryCascaderOptions.value.length) return null
  const id = String(geonameid).trim()
  if (!id) return null
  return findGeonameidPathInCascaderTree(globalCountryCascaderOptions.value, id)
}

/** 在缓存树中按 id 查找节点 */
export function findGlobalAreaNodeById(
  geonameid: number,
  nodes: GlobalAreaTreeNode[] = globalCountryTreeData.value,
): GlobalAreaTreeNode | null {
  for (const n of nodes) {
    if (n.geonameid === geonameid) return n
    if (n.children?.length) {
      const hit = findGlobalAreaNodeById(geonameid, n.children)
      if (hit) return hit
    }
  }
  return null
}

/** 获取级联选项（仅走缓存模块加载） */
export async function getGlobalCountryCascaderOptions(): Promise<OverseasGeoCascaderOption[]> {
  await ensureGlobalCountryTree()
  return globalCountryCascaderOptions.value
}

/** 获取原始树（仅走缓存模块加载） */
export async function getGlobalCountryTreeData(): Promise<GlobalAreaTreeNode[]> {
  await ensureGlobalCountryTree()
  return globalCountryTreeData.value
}
